import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Collapse,
} from "@mui/material";
import { HistoryItem } from "../../types/history";
import { formatDateTime } from "../../Helpers/Helpers";
import CommonDialog from "../Shared/CommonDialog/CommonDialog";
import { createInteractionLog } from "../../Services/interactionLogService";
import styles from "./InteractionInterfaceBox.module.scss";

type Props = { history: HistoryItem[] };

export const InteractionInterfaceBox = ({ history }: Props) => {
    const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
    const [openSaveDialog, setOpenSaveDialog] = useState(false);
    const [fileName, setFileName] = useState("interaction-history");

    const toggle = (id: string) =>
        setOpenMap((m) => ({ ...m, [id]: !m[id] }));

    const handleSaveToFile = async () => {
        const contents = history.map(
            (item, i) =>
                `${i + 1}. ${item.time}: ${item.label}${
                    item.detail ? " - " + item.detail : ""
                }`
        );

        const endTime = formatDateTime();
        const endAction = `${contents.length + 1}. ${endTime}: Save Action`;
        const downloadContent = [...contents, endAction].join("\n");

        const jsonContent = JSON.stringify([...contents, endAction]);

        await createInteractionLog({
            fileName: `${fileName || "interaction-history"}.json`,
            content: jsonContent,
        });

        const blob = new Blob([downloadContent], {
            type: "text/plain;charset=utf-8",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName || "interaction-history"}.txt`;
        link.click();

        setOpenSaveDialog(false);
    };

    const handleOpenDialog = () => {
        const scamStarted = localStorage.getItem("scamStarted");
        if (scamStarted) {
            setOpenSaveDialog(true);
        }
    };

    return (
        <Box height="100%" className="MuiSideBar interaction-interface-box">
            <Box className="titleBox">
                <Typography variant="subtitle2" className={`titleText ${styles.titleText}` }>
                    Interaction History
                </Typography>
            </Box>

            {/* History list */}
            <Box className={styles.historyContainer}>
                <List>
                    {history.map((item, index) => {
                        const num = index + 1;

                        if (item.kind === "link") {
                            return (
                                <Box key={item.id}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => toggle(item.id)}
                                            className={styles.linkButton}
                                        >
                                            <ListItemText
                                                primary={`${num}. ${item.time} : ${item.label}`}
                                                primaryTypographyProps={{
                                                    className: styles.linkText,
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>

                                    <Collapse
                                        in={!!openMap[item.id]}
                                        timeout="auto"
                                        unmountOnExit
                                    >
                                        <ListItemText>
                                            <Typography
                                                variant="body2"
                                                component="span"
                                                className={`MuiListItemText-primary ${styles.collapseDetailText}`}
                                                dangerouslySetInnerHTML={{
                                                    __html: item?.detail ?? "",
                                                }}
                                            />
                                        </ListItemText>
                                    </Collapse>
                                </Box>
                            );
                        }

                        return (
                            <ListItem key={item.id} disablePadding>
                                <ListItemText
                                    primary={`${num}. ${item.time} : ${item.label}`}
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </Box>

            {/* Save button */}
            <Box className={styles.saveButtonWrapper}>
                <Button
                    variant="contained"
                    className={`${styles.saveButton} save-history-btn`}
                    onClick={handleOpenDialog}
                >
                    Save
                </Button>
            </Box>

            {/* Save dialog */}
            <CommonDialog
                open={openSaveDialog}
                title="Save Interaction History"
                imageSrc="/assets/cyborg-bear-1.png"
                content={
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <Typography>Enter file name:</Typography>
                        <input
                            type="text"
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)}
                            style={{
                                padding: "8px",
                                fontSize: "14px",
                                borderRadius: "6px",
                                border: "1px solid rgba(0,0,0,0.2)",
                            }}
                        />
                    </Box>
                }
                onConfirm={handleSaveToFile}
                onClose={() => setOpenSaveDialog(false)}
                confirmText="Save"
                cancelText="Cancel"
            />
        </Box>
    );
};
